/**
 * 小标打印（platform=Printmaster）由 APP 在 WebView 打开 H5 时，通过 URL Query 传递的授权与状态字段。
 * APP 请仅使用下方 PRINTMASTER_URL_PARAM 中的 key 传参；值为字符串，布尔含义见 parseQueryBoolean。
 *
 * 示例（与现有 userId、platform 等参数并列）：
 * #/?platform=Printmaster&userId=...&pmAlbum=1&pmFeedback=1&pmFeedbackTime=1715616000000&pmPrivacy=1
 */

/** @typedef {'1'|'0'|'true'|'false'|string|undefined} QueryScalar */

/**
 * URL 参数名（与 APP 约定，勿随意改名）
 * @readonly
 * @enum {string}
 */
export const PRINTMASTER_URL_PARAM = Object.freeze({
  /** 相册/图片是否已授权（系统权限管理为已授权） */
  ALBUM: "pmAlbum",
  /** 麦克风是否已授权 */
  MIC: "pmMic",
  /** 摄像头是否已授权 */
  CAMERA: "pmCamera",
  /** 是否曾提交意见反馈：1 有，0 无 */
  FEEDBACK: "pmFeedback",
  /**
   * 最近一次意见反馈提交时间（建议有反馈时必传）
   * 支持：2026/05/13、2026-05-13、20260513 等，展示为 YYYY/MM/DD 提交
   */
  FEEDBACK_TIME: "pmFeedbackTime",
  /** 蓝牙是否已授权（用于连接打印机等场景，对应「位置信息」行） */
  BLUETOOTH: "pmBluetooth",
  /** 是否已同意隐私政策（为 true 时，设备信息列表全部条目的收集情况与信息内容按「已同意隐私政策」展示） */
  PRIVACY: "pmPrivacy",
})

const PARAM_VALUES = Object.freeze(Object.values(PRINTMASTER_URL_PARAM))

export const PRINTMASTER_URL_PARAM_DOCS = Object.freeze([
  { param: PRINTMASTER_URL_PARAM.ALBUM, type: "0 / 1", desc: "相册（图片）系统授权状态" },
  { param: PRINTMASTER_URL_PARAM.MIC, type: "0 / 1", desc: "麦克风授权状态" },
  { param: PRINTMASTER_URL_PARAM.CAMERA, type: "0 / 1", desc: "摄像头授权状态" },
  { param: PRINTMASTER_URL_PARAM.FEEDBACK, type: "0 / 1", desc: "是否提交过意见反馈" },
  { param: PRINTMASTER_URL_PARAM.FEEDBACK_TIME, type: "string | number", desc: "最近一次反馈时间：Unix 秒/毫秒时间戳，或 YYYY-MM-DD / YYYYMMDD" },
  { param: PRINTMASTER_URL_PARAM.BLUETOOTH, type: "0 / 1", desc: "蓝牙授权（位置信息行）" },
  { param: PRINTMASTER_URL_PARAM.PRIVACY, type: "0 / 1", desc: "是否同意隐私政策；同意时设备信息页各条收集情况与信息内容统一为隐私政策态" },
])

/** 与产品表一致的展示文案 */
export const PRINTMASTER_AUTH_COPY = Object.freeze({
  COLLECT_AUTH_STOP:
    "在您完成授权的情况下收集；当您取消授权或注销账户后，停止收集",
  PIC_CONTENT:
    "在获得您的授权后，当您在标签编辑器中上传图片时，小标打印将会收集您的图片作为信息的一部分",
  LOC_CONTENT:
    "在获得您的授权后，当您连接打印机时，小标打印将会收集您的位置信息作为信息的一部分",
  MIC_CAM_COLLECTED: "为实现服务目的一次性使用，不存储",
  MIC_CAM_VALUE: "具体信息内容已删除",
  PRIVACY_COLLECT:
    "在您同意隐私政策协议的情况下进行收集，当您撤销授权或注销账户后停止收集",
  PRIVACY_VALUE: "可前往\"设置-关于本机\"查看详情",
})

function parseQueryBoolean(raw) {
  if (raw === undefined || raw === null) return undefined
  const s = String(raw).trim().toLowerCase()
  if (s === "" || s === "undefined" || s === "null") return undefined
  if (s === "1" ) return true
  if (s === "0") return false
  return undefined
}

/**
 * @param {Record<string, QueryScalar>} query
 * @returns {boolean}
 */
export function hasPrintmasterAuthParams(query) {
  if (!query || typeof query !== "object") return false
  return PARAM_VALUES.some((k) => query[k] !== undefined && String(query[k]).trim() !== "")
}

/**
 * 仅解析 URL 中出现的字段；未出现的字段在返回对象中不存在该 key（merge 时保留 JSON 默认）
 * @param {Record<string, QueryScalar>} query
 * @returns {Record<string, boolean|string|undefined>}
 */
export function parsePrintmasterAuthQuery(query) {
  const out = {}
  const b = (param) => {
    const raw = query[param]
    if (raw === undefined || raw === null || String(raw).trim() === "") return undefined
    return parseQueryBoolean(raw)
  }
  const album = b(PRINTMASTER_URL_PARAM.ALBUM)
  if (album !== undefined) out.album = album
  const mic = b(PRINTMASTER_URL_PARAM.MIC)
  if (mic !== undefined) out.mic = mic
  const camera = b(PRINTMASTER_URL_PARAM.CAMERA)
  if (camera !== undefined) out.camera = camera
  const bluetooth = b(PRINTMASTER_URL_PARAM.BLUETOOTH)
  if (bluetooth !== undefined) out.bluetooth = bluetooth
  const privacy = b(PRINTMASTER_URL_PARAM.PRIVACY)
  if (privacy !== undefined) out.privacy = privacy
  const feedback = b(PRINTMASTER_URL_PARAM.FEEDBACK)
  if (feedback !== undefined) out.feedback = feedback
  if (query[PRINTMASTER_URL_PARAM.FEEDBACK_TIME] != null && String(query[PRINTMASTER_URL_PARAM.FEEDBACK_TIME]).trim() !== "") {
    out.feedbackTime = String(query[PRINTMASTER_URL_PARAM.FEEDBACK_TIME]).trim()
  }
  return out
}

/**
 * 将 pmFeedbackTime 格式化为 YYYY/MM/DD。
 * 支持：Unix 秒（通常 10 位）、Unix 毫秒（通常 13 位）、YYYYMMDD（8 位纯数字）、YYYY-MM-DD / YYYY/MM/DD 字符串。
 * @param {string|number} raw
 * @returns {string}
 */
export function formatPrintmasterFeedbackTime(raw) {
  if (raw === undefined || raw === null || raw === "") return ""
  const s0 = String(raw).trim()
  if (!s0) return ""

  if (/^\d+$/.test(s0)) {
    if (s0.length === 8) {
      return `${s0.slice(0, 4)}/${s0.slice(4, 6)}/${s0.slice(6, 8)}`
    }
    const n = parseInt(s0, 10)
    if (!Number.isNaN(n)) {
      const ms = s0.length <= 10 ? n * 1000 : n
      const d = new Date(ms)
      if (!Number.isNaN(d.getTime())) {
        const y = d.getFullYear()
        const mo = String(d.getMonth() + 1).padStart(2, "0")
        const day = String(d.getDate()).padStart(2, "0")
        return `${y}/${mo}/${day}`
      }
    }
  }

  const s = s0.replace(/-/g, "/")
  const m = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/)
  if (m) {
    const y = m[1]
    const mo = m[2].padStart(2, "0")
    const d = m[3].padStart(2, "0")
    return `${y}/${mo}/${d}`
  }
  const digits = s.replace(/\D/g, "")
  if (digits.length === 8) {
    return `${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6, 8)}`
  }
  return s0
}

function cloneRow(row) {
  return JSON.parse(JSON.stringify(row))
}

/**
 * @param {object[]} baseList applyInfo.json Printmaster 数组
 * @param {Record<string, boolean|string>} auth parsePrintmasterAuthQuery 结果
 * @returns {object[]}
 */
export function mergePrintmasterApplyInfoRows(baseList, auth) {
  if (!baseList || !auth || typeof auth !== "object") return baseList
  return baseList.map((row) => {
    const item = cloneRow(row)
    const name = item.name

    if (name === "图片" && auth.album !== undefined) {
      delete item.apiKey
      if (auth.album) {
        item.isCollected = PRINTMASTER_AUTH_COPY.COLLECT_AUTH_STOP
        item.value = PRINTMASTER_AUTH_COPY.PIC_CONTENT
      } else {
        item.isCollected = false
        item.value = "无"
      }
      return item
    }

    if (name === "麦克风" && auth.mic !== undefined) {
      delete item.apiKey
      if (auth.mic) {
        item.isCollected = PRINTMASTER_AUTH_COPY.MIC_CAM_COLLECTED
        item.value = PRINTMASTER_AUTH_COPY.MIC_CAM_VALUE
      } else {
        item.isCollected = false
        item.value = "无"
      }
      return item
    }

    if (name === "摄像头" && auth.camera !== undefined) {
      delete item.apiKey
      if (auth.camera) {
        item.isCollected = PRINTMASTER_AUTH_COPY.MIC_CAM_COLLECTED
        item.value = PRINTMASTER_AUTH_COPY.MIC_CAM_VALUE
      } else {
        item.isCollected = false
        item.value = "无"
      }
      return item
    }

    if (name === "用户反馈信息" && auth.feedback !== undefined) {
      delete item.apiKey
      if (auth.feedback) {
        item.isCollected = true
        const t = auth.feedbackTime ? formatPrintmasterFeedbackTime(auth.feedbackTime) : ""
        item.value = t ? `${t} 提交` : "已提交意见反馈"
      } else {
        item.isCollected = false
        item.value = "无"
      }
      return item
    }

    if (name === "客服信息") {
      delete item.apiKey
      item.isCollected = false
      item.value = "暂不支持"
      return item
    }

    if (name === "位置信息" && auth.bluetooth !== undefined) {
      delete item.apiKey
      if (auth.bluetooth) {
        item.isCollected = PRINTMASTER_AUTH_COPY.COLLECT_AUTH_STOP
        item.value = PRINTMASTER_AUTH_COPY.LOC_CONTENT
      } else {
        item.isCollected = false
        item.value = "无"
      }
      return item
    }

    return item
  })
}

/**
 * 当 URL 传入 pmPrivacy 时：同意则全部设备信息行的「收集情况」「信息内容」统一为隐私政策态；不同意则统一为未收集 / 无。
 * @param {object[]} baseList 已做过 SDK / Android 过滤后的列表
 * @param {Record<string, boolean|string>} auth
 * @returns {object[]}
 */
export function mergePrintmasterDeviceInfoRows(baseList, auth) {
  if (!baseList || !auth || typeof auth !== "object" || auth.privacy === undefined) {
    return baseList
  }
  return baseList.map((row) => {
    const item = cloneRow(row)
    if (auth.privacy) {
      item.isCollected = PRINTMASTER_AUTH_COPY.PRIVACY_COLLECT
      item.value = PRINTMASTER_AUTH_COPY.PRIVACY_VALUE
    } else {
      item.isCollected = false
      item.value = "无"
    }
    return item
  })
}
